<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-database text-indigo-600 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Synchronisation des bases de données</h1>
              <p class="text-sm text-gray-500 mt-1">MSSQL ↔ MySQL — Gestion complète de la synchronisation</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button
              @click="testConnection"
              :disabled="testing || loading"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="testing ? 'fas fa-spinner fa-spin' : 'fas fa-check-circle'" class="mr-2"></i>
              {{ testing ? 'Test en cours...' : 'Tester connexions' }}
            </button>
            <button
              @click="syncAllTables"
              :disabled="syncing || loading"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              <span v-if="syncing && currentSyncLevel">Niveau {{ currentSyncLevel }}/{{ totalSyncLevels }}...</span>
              <span v-else-if="syncing">Synchronisation...</span>
              <span v-else>Tout synchroniser (MSSQL → MySQL)</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      <!-- ══════════════════════════════════════════════════════════════
           SECTION 1 : Synchronisation unidirectionnelle MSSQL → MySQL
           ══════════════════════════════════════════════════════════════ -->
      <section>
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-100">
              <i class="fas fa-arrow-right text-indigo-600"></i>
            </div>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">MSSQL → MySQL <span class="text-sm font-normal text-gray-500">(unidirectionnel)</span></h2>
              <p class="text-xs text-gray-500">Tables de référence importées depuis MSSQL</p>
            </div>
          </div>
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            {{ uniTables.length }} tables
          </span>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-12">
          <i class="fas fa-spinner fa-spin text-indigo-600 text-5xl"></i>
          <p class="mt-4 text-gray-600">Chargement des tables...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <i class="fas fa-exclamation-circle text-red-400 text-xl"></i>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Erreur</h3>
              <p class="mt-1 text-sm text-red-700">{{ error }}</p>
            </div>
          </div>
        </div>

        <!-- Tables Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="table in uniTables"
            :key="table.name"
            class="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200"
            :class="{ 'ring-2 ring-indigo-300 ring-offset-2': isTableSyncing(table.name) }"
          >
            <div class="p-6">
              <!-- Table Header -->
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <i v-if="isTableSyncing(table.name)" class="fas fa-spinner fa-spin text-indigo-400 text-sm"></i>
                  {{ table.name }}
                </h3>
                <span
                  :class="{
                    'bg-green-100 text-green-800': table.status === 'synced' && !isTableSyncing(table.name),
                    'bg-yellow-100 text-yellow-800': table.status === 'outdated' && !isTableSyncing(table.name),
                    'bg-red-100 text-red-800': table.status === 'error' && !isTableSyncing(table.name),
                    'bg-indigo-100 text-indigo-700': isTableSyncing(table.name),
                  }"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                >
                  <i v-if="isTableSyncing(table.name)" class="fas fa-spinner fa-spin mr-1"></i>
                  <i v-else-if="table.status === 'synced'" class="fas fa-check-circle mr-1"></i>
                  <i v-else-if="table.status === 'outdated'" class="fas fa-exclamation-triangle mr-1"></i>
                  <i v-else class="fas fa-times-circle mr-1"></i>
                  {{ isTableSyncing(table.name) ? 'Synchronisation...' : statusLabel(table.status) }}
                </span>
              </div>

              <!-- Error Message (if any) -->
              <div v-if="table.status === 'error' && table.error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div class="flex items-start">
                  <i class="fas fa-exclamation-circle text-red-400 mt-0.5 flex-shrink-0"></i>
                  <div class="ml-2">
                    <p class="text-xs font-medium text-red-800">Erreur détaillée:</p>
                    <p class="mt-1 text-xs text-red-700 break-words">{{ table.error }}</p>
                  </div>
                </div>
              </div>

              <!-- Statistics -->
              <div class="space-y-2 mb-4">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">MSSQL <span class="text-xs text-indigo-500">(source)</span>:</span>
                  <span class="font-semibold text-gray-900">{{ table.mssqlCount !== undefined ? table.mssqlCount.toLocaleString() : 'N/A' }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">MySQL <span class="text-xs text-gray-400">(cible)</span>:</span>
                  <span class="font-semibold text-gray-900">{{ table.mysqlCount !== undefined ? table.mysqlCount.toLocaleString() : 'N/A' }}</span>
                </div>
                <div v-if="table.difference !== undefined && table.difference !== 0" class="flex justify-between text-sm">
                  <span class="text-gray-600">Différence:</span>
                  <span :class="table.difference > 0 ? 'text-yellow-600' : 'text-green-600'" class="font-semibold">
                    {{ table.difference > 0 ? '+' : '' }}{{ table.difference.toLocaleString() }}
                  </span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Dernière sync:</span>
                  <span class="text-gray-900">{{ table.lastSync || 'Jamais' }}</span>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex gap-2">
                <button
                  @click="compareTable(table.name)"
                  :disabled="syncing || isTableSyncing(table.name)"
                  class="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i class="fas fa-clipboard-list mr-1.5"></i>
                  Comparer
                </button>
                <button
                  @click="syncTable(table.name)"
                  :disabled="syncing || isTableSyncing(table.name)"
                  class="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i :class="isTableSyncing(table.name) ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-1.5"></i>
                  Sync
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════
           SECTION 2 : Synchronisation bi-directionnelle
           (visit, control, comment)
           ══════════════════════════════════════════════════════════════ -->
      <section>
        <!-- Section header -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-purple-100">
              <i class="fas fa-exchange-alt text-purple-600"></i>
            </div>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">
                Synchronisation bi-directionnelle
                <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                  ↔ MSSQL ↔ MySQL
                </span>
              </h2>
              <p class="text-xs text-gray-500">Tables visit, control, comment — données synchronisées dans les deux sens</p>
            </div>
          </div>
          <!-- Global bidirectional sync button -->
          <button
            @click="syncAllBiDir"
            :disabled="biDirSyncing || syncing"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i :class="biDirSyncing ? 'fas fa-spinner fa-spin' : 'fas fa-exchange-alt'" class="mr-2"></i>
            <span v-if="biDirSyncing">Sync bi-dir en cours...</span>
            <span v-else>Tout synchroniser (bi-directionnel)</span>
          </button>
        </div>

        <!-- Info banner -->
        <div class="mb-5 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div class="flex items-start gap-3">
            <i class="fas fa-info-circle text-purple-500 mt-0.5 flex-shrink-0"></i>
            <div class="text-sm text-purple-800 space-y-1">
              <p>
                <strong>Ces 3 tables sont synchronisées dans les deux sens</strong>, contrairement aux tables de référence ci-dessus.
              </p>
              <ul class="list-disc list-inside text-purple-700 space-y-0.5 mt-1">
                <li><strong>MSSQL → MySQL</strong> : importe les visites/contrôles/commentaires créés dans MSSQL (base de référence)</li>
                <li><strong>MySQL → MSSQL</strong> : remonte les visites/contrôles/commentaires créés via la PWA vers MSSQL</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="biDirLoading" class="text-center py-10">
          <i class="fas fa-spinner fa-spin text-purple-600 text-4xl"></i>
          <p class="mt-3 text-gray-600">Chargement des statistiques bi-directionnelles...</p>
        </div>

        <!-- BiDir Error State -->
        <div v-else-if="biDirError" class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <i class="fas fa-exclamation-circle text-red-400 text-xl"></i>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Erreur</h3>
              <p class="mt-1 text-sm text-red-700">{{ biDirError }}</p>
            </div>
          </div>
        </div>

        <!-- BiDir Tables Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            v-for="table in biDirTables"
            :key="table.name"
            class="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border-t-4 border-purple-400"
          >
            <div class="p-5">
              <!-- Table name -->
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <i class="fas fa-table text-purple-400 text-sm"></i>
                  {{ table.name }}
                </h3>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  bi-dir
                </span>
              </div>

              <!-- Direction: MSSQL → MySQL -->
              <div class="mb-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                <div class="flex items-center gap-1.5 mb-2">
                  <i class="fas fa-arrow-right text-indigo-500 text-xs"></i>
                  <span class="text-xs font-semibold text-indigo-700 uppercase tracking-wide">MSSQL → MySQL</span>
                  <span
                    :class="{
                      'bg-green-100 text-green-700': table.mssqlToMysql.status === 'synced',
                      'bg-yellow-100 text-yellow-700': table.mssqlToMysql.status === 'outdated',
                      'bg-red-100 text-red-700': table.mssqlToMysql.status === 'error',
                      'bg-gray-100 text-gray-600': table.mssqlToMysql.status === 'loading',
                    }"
                    class="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ statusLabel(table.mssqlToMysql.status) }}
                  </span>
                </div>
                <div class="space-y-1 text-xs mb-2">
                  <div class="flex justify-between">
                    <span class="text-indigo-600">Source (MSSQL) :</span>
                    <span class="font-semibold text-gray-800">{{ formatCount(table.mssqlToMysql.mssqlCount) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-indigo-600">Cible (MySQL) :</span>
                    <span class="font-semibold text-gray-800">{{ formatCount(table.mssqlToMysql.mysqlCount) }}</span>
                  </div>
                  <div v-if="table.mssqlToMysql.difference !== undefined && table.mssqlToMysql.difference !== 0" class="flex justify-between">
                    <span class="text-indigo-600">Différence :</span>
                    <span :class="table.mssqlToMysql.difference > 0 ? 'text-yellow-600' : 'text-green-600'" class="font-semibold">
                      {{ table.mssqlToMysql.difference > 0 ? '+' : '' }}{{ formatCount(table.mssqlToMysql.difference) }}
                    </span>
                  </div>
                </div>
                <div class="flex gap-1.5">
                  <button
                    @click="compareTable(table.name)"
                    :disabled="syncing || biDirSyncing"
                    class="flex-1 inline-flex justify-center items-center px-2 py-1.5 border border-indigo-300 text-xs font-medium rounded text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i class="fas fa-clipboard-list mr-1"></i>
                    Comparer
                  </button>
                  <button
                    @click="syncBiDirTable(table.name, 'mssql-to-mysql')"
                    :disabled="syncing || biDirSyncing"
                    class="flex-1 inline-flex justify-center items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i :class="biDirSyncingTable === table.name + '-mssql-to-mysql' ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-1"></i>
                    Sync →
                  </button>
                </div>
              </div>

              <!-- Direction: MySQL → MSSQL -->
              <div class="p-3 rounded-lg bg-orange-50 border border-orange-100">
                <div class="flex items-center gap-1.5 mb-2">
                  <i class="fas fa-arrow-left text-orange-500 text-xs"></i>
                  <span class="text-xs font-semibold text-orange-700 uppercase tracking-wide">MySQL → MSSQL</span>
                  <span
                    :class="{
                      'bg-green-100 text-green-700': table.mysqlToMssql.status === 'synced',
                      'bg-yellow-100 text-yellow-700': table.mysqlToMssql.status === 'outdated',
                      'bg-red-100 text-red-700': table.mysqlToMssql.status === 'error',
                      'bg-gray-100 text-gray-600': table.mysqlToMssql.status === 'loading',
                    }"
                    class="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ statusLabel(table.mysqlToMssql.status) }}
                  </span>
                </div>
                <div class="space-y-1 text-xs mb-2">
                  <div class="flex justify-between">
                    <span class="text-orange-600">Source (MySQL) :</span>
                    <span class="font-semibold text-gray-800">{{ formatCount(table.mysqlToMssql.mysqlCount) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-orange-600">Cible (MSSQL) :</span>
                    <span class="font-semibold text-gray-800">{{ formatCount(table.mysqlToMssql.mssqlCount) }}</span>
                  </div>
                  <div v-if="table.mysqlToMssql.difference !== undefined && table.mysqlToMssql.difference !== 0" class="flex justify-between">
                    <span class="text-orange-600">Différence :</span>
                    <span :class="table.mysqlToMssql.difference > 0 ? 'text-orange-600' : 'text-green-600'" class="font-semibold">
                      {{ table.mysqlToMssql.difference > 0 ? '+' : '' }}{{ formatCount(table.mysqlToMssql.difference) }}
                    </span>
                  </div>
                </div>
                <button
                  @click="syncBiDirTable(table.name, 'mysql-to-mssql')"
                  :disabled="syncing || biDirSyncing"
                  class="w-full inline-flex justify-center items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i :class="biDirSyncingTable === table.name + '-mysql-to-mssql' ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-1"></i>
                  <span v-if="biDirSyncingTable === table.name + '-mysql-to-mssql'">Synchronisation...</span>
                  <span v-else>Sync ←</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════
           SECTION 3 : Synchronisation PWA (IndexedDB)
           ══════════════════════════════════════════════════════════════ -->
      <section class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <i class="fas fa-mobile-alt text-indigo-600 text-xl mr-3"></i>
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Synchronisation PWA (IndexedDB)</h2>
                <p class="text-sm text-gray-500 mt-0.5">Gestion des dates de baseline pour la sync incrémentale côté navigateur</p>
              </div>
            </div>
            <button
              @click="loadPwaSyncStatus"
              :disabled="pwaSyncLoading"
              class="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
            >
              <i :class="pwaSyncLoading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
              Actualiser
            </button>
          </div>
        </div>

        <div class="px-6 py-4">
          <!-- Alerte contextuelle si sync en cours -->
          <div v-if="pwaSyncInProgress" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2">
            <i class="fas fa-exclamation-triangle text-yellow-500 mt-0.5 flex-shrink-0"></i>
            <div class="text-sm text-yellow-800">
              <strong>Sync incrémentale en cours</strong> — des pages se téléchargent. Utiliser "Réinitialiser à maintenant" pour stopper et reprendre depuis la date actuelle.
            </div>
          </div>

          <!-- Tableau des entités -->
          <div class="overflow-x-auto mb-5">
            <table class="min-w-full divide-y divide-gray-200 text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Entité</th>
                  <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Dernière sync</th>
                  <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Ancienneté</th>
                  <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-if="pwaSyncLoading">
                  <td colspan="4" class="px-4 py-4 text-center text-gray-400">
                    <i class="fas fa-spinner fa-spin mr-2"></i>Chargement...
                  </td>
                </tr>
                <tr v-else-if="pwaSyncStatuses.length === 0">
                  <td colspan="4" class="px-4 py-4 text-center text-gray-400">Aucune donnée de sync locale trouvée</td>
                </tr>
                <tr v-else v-for="status in pwaSyncStatuses" :key="status.entity">
                  <td class="px-4 py-2 font-medium text-gray-700">{{ status.entity }}</td>
                  <td class="px-4 py-2 text-gray-600">{{ status.lastSync ? formatDateTime(status.lastSync) : '—' }}</td>
                  <td class="px-4 py-2 text-gray-600">{{ status.lastSync ? getRelativeTime(status.lastSync) : '—' }}</td>
                  <td class="px-4 py-2">
                    <span
                      :class="{
                        'bg-green-100 text-green-800': status.status === 'fresh',
                        'bg-blue-100 text-blue-800': status.status === 'recent',
                        'bg-yellow-100 text-yellow-800': status.status === 'ok',
                        'bg-red-100 text-red-800': status.status === 'outdated',
                        'bg-gray-100 text-gray-800': status.status === 'never' || status.status === 'unknown',
                      }"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ pwaSyncStatusLabel(status.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              @click="resetPwaSyncToNow"
              :disabled="pwaSyncResetting"
              class="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="pwaSyncResetting ? 'fas fa-spinner fa-spin' : 'fas fa-clock'" class="mr-2"></i>
              Réinitialiser les dates à maintenant
            </button>
            <button
              @click="resetPwaSyncFull"
              :disabled="pwaSyncResetting"
              class="inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-trash-alt mr-2"></i>
              Forcer une resynchronisation complète
            </button>
          </div>

          <!-- Description des actions -->
          <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-500">
            <div class="p-3 bg-orange-50 rounded-md border border-orange-100">
              <strong class="text-orange-700">Réinitialiser à maintenant</strong><br>
              Met la baseline de sync à l'heure actuelle. La prochaine sync incrémentale sera vide (0 page). Les données en cache ne sont pas supprimées.
              <br><em class="text-orange-600">Utile pour stopper une sync massive après un import MSSQL.</em>
            </div>
            <div class="p-3 bg-red-50 rounded-md border border-red-100">
              <strong class="text-red-700">Forcer une resync complète</strong><br>
              Supprime toutes les dates de baseline. Au prochain lancement, la PWA retéléchargera toutes les données depuis l'API (load initial).
              <br><em class="text-red-600">À utiliser uniquement si les données sont corrompues.</em>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════
           SECTION 4 : Logs
           ══════════════════════════════════════════════════════════════ -->
      <div v-if="logs.length > 0" class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Logs de synchronisation</h2>
            <button
              @click="clearLogs"
              class="text-sm text-gray-600 hover:text-gray-900"
            >
              Effacer
            </button>
          </div>
        </div>
        <div class="px-6 py-4 max-h-96 overflow-y-auto">
          <div v-for="(log, index) in logs" :key="index" class="flex items-start py-2 text-sm">
            <span class="text-gray-500 mr-3 flex-shrink-0">{{ log.time }}</span>
            <i
              v-if="log.type === 'success'"
              class="fas fa-check-circle text-green-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else-if="log.type === 'error'"
              class="fas fa-times-circle text-red-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else
              class="fas fa-info-circle text-blue-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <span :class="log.type === 'error' ? 'text-red-700' : 'text-gray-700'">{{ log.message }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- ══════════════════════════════════════════════════════════════
         Comparison Modal
         ══════════════════════════════════════════════════════════════ -->
    <div v-if="showCompareModal" class="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-compare-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-40" aria-hidden="true" @click="showCompareModal = false"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-50">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-compare-title">
                  Comparaison : {{ compareData?.table }}
                </h3>
                <div class="mt-4 space-y-3">
                  <div v-if="compareLoading" class="text-center py-4">
                    <i class="fas fa-spinner fa-spin text-indigo-600 text-3xl"></i>
                  </div>
                  <template v-else-if="compareData">
                    <div class="bg-gray-50 rounded p-3">
                      <div class="flex justify-between mb-2">
                        <span class="text-sm text-gray-600">Enregistrements MSSQL:</span>
                        <span class="text-sm font-semibold">{{ getNestedValue(compareData, 'statistics.mssqlCount', 0) }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm text-gray-600">Enregistrements MySQL:</span>
                        <span class="text-sm font-semibold">{{ getNestedValue(compareData, 'statistics.mysqlCount', 0) }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm text-gray-600">Enregistrements modifiés:</span>
                        <span class="text-sm font-semibold text-yellow-600">{{ compareData.changedRecords || 0 }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm text-gray-600">Colonne de timestamp:</span>
                        <span class="text-sm font-semibold">{{ compareData.timestampColumn || 'Aucune' }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-sm text-gray-600">Sync incrémentale possible:</span>
                        <span class="text-sm font-semibold" :class="compareData.canIncrementalSync ? 'text-green-600' : 'text-gray-600'">
                          {{ compareData.canIncrementalSync ? 'Oui' : 'Non' }}
                        </span>
                      </div>
                    </div>
                    <div class="mt-3 p-2 bg-blue-50 rounded text-xs">
                      <details>
                        <summary class="cursor-pointer text-blue-700 font-medium">Données brutes (debug)</summary>
                        <pre class="mt-2 text-xs overflow-auto">{{ JSON.stringify(compareData, null, 2) }}</pre>
                      </details>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              @click="showCompareModal = false"
              class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         Custom Confirm Modal (remplace window.confirm)
         ══════════════════════════════════════════════════════════════ -->
    <transition name="modal-fade">
      <div
        v-if="confirmModal.show"
        class="fixed z-[60] inset-0 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="'modal-confirm-title'"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-gray-900 bg-opacity-60 transition-opacity"
          @click="confirmModalCancel"
        ></div>

        <!-- Dialog -->
        <div class="flex items-center justify-center min-h-screen p-4">
          <div
            class="relative bg-white rounded-xl shadow-2xl w-full max-w-md z-10 overflow-hidden"
            @click.stop
          >
            <!-- Header -->
            <div
              class="px-6 pt-6 pb-4 flex items-start gap-4"
              :class="confirmModal.danger ? 'border-b border-red-100' : 'border-b border-gray-100'"
            >
              <div
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                :class="confirmModal.danger ? 'bg-red-100' : 'bg-indigo-100'"
              >
                <i
                  class="text-lg"
                  :class="confirmModal.danger ? 'fas fa-exclamation-triangle text-red-600' : 'fas fa-question-circle text-indigo-600'"
                ></i>
              </div>
              <div class="flex-1 min-w-0">
                <h3
                  id="modal-confirm-title"
                  class="text-base font-semibold"
                  :class="confirmModal.danger ? 'text-red-900' : 'text-gray-900'"
                >
                  {{ confirmModal.title }}
                </h3>
                <p
                  v-if="confirmModal.message"
                  class="mt-1 text-sm text-gray-600"
                >
                  {{ confirmModal.message }}
                </p>
              </div>
            </div>

            <!-- Optional bullet list -->
            <div v-if="confirmModal.lines && confirmModal.lines.length" class="px-6 py-3 bg-gray-50">
              <ul class="space-y-1">
                <li
                  v-for="(line, i) in confirmModal.lines"
                  :key="i"
                  class="flex items-start gap-2 text-sm text-gray-700"
                >
                  <i class="fas fa-circle-arrow-right text-indigo-400 mt-0.5 flex-shrink-0 text-xs"></i>
                  <span>{{ line }}</span>
                </li>
              </ul>
            </div>

            <!-- Actions -->
            <div class="px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                @click="confirmModalCancel"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                @click="confirmModalAccept"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                :class="confirmModal.danger
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'"
              >
                <i class="fas fa-check mr-1.5"></i>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import api from '@/services/api'
import syncService from '@/services/syncService'

// Tables avec synchronisation bi-directionnelle (MSSQL ↔ MySQL)
const BIDIRECTIONAL_TABLES = ['visit', 'control', 'comment']

export default {
  name: 'DatabaseSync',
  
  data() {
    return {
      // ── Tables unidirectionnelles (MSSQL → MySQL) ──────────────────────────
      tables: [],         // toutes les tables (chargées depuis l'API)
      loading: false,
      syncing: false,
      testing: false,
      currentSyncingTable: null,
      currentSyncLevel: null,
      totalSyncLevels: 0,
      error: null,
      /** Set de noms de tables actuellement en cours de sync (pour spinners par table) */
      syncingTablesArray: [],

      // ── Tables bi-directionnelles (visit, control, comment) ────────────────
      biDirTables: [],
      biDirLoading: false,
      biDirSyncing: false,
      biDirSyncingTable: null,  // 'tableName-direction' ex: 'visit-mssql-to-mysql'
      biDirError: null,

      // ── Logs & modaux ──────────────────────────────────────────────────────
      logs: [],
      showCompareModal: false,
      compareData: null,
      compareLoading: false,

      // ── Custom confirm modal ───────────────────────────────────────────────
      confirmModal: {
        show: false,
        title: '',
        message: '',
        lines: [],
        danger: false,
        resolve: null,
      },

      // ── PWA sync ──────────────────────────────────────────────────────────
      pwaSyncStatuses: [],
      pwaSyncLoading: false,
      pwaSyncResetting: false,
      pwaSyncInProgress: false,
    }
  },

  computed: {
    /** Tables unidirectionnelles : tout sauf les tables bi-directionnelles */
    uniTables() {
      return this.tables.filter(t => !BIDIRECTIONAL_TABLES.includes(t.name))
    },
  },

  mounted() {
    this.loadTablesProgressively()
    this.loadBiDirStats()
    this.loadPwaSyncStatus()
  },

  methods: {

    // ════════════════════════════════════════════════════════════════════════
    // Custom confirm modal (remplace window.confirm)
    // ════════════════════════════════════════════════════════════════════════

    /**
     * Affiche une modale de confirmation et retourne une Promise<boolean>.
     * Utilisation : if (!await this.showConfirm('Titre', 'Message')) return
     *
     * @param {string} title
     * @param {string} message
     * @param {object} options  - { lines: string[], danger: boolean }
     */
    showConfirm(title, message = '', { lines = [], danger = false } = {}) {
      return new Promise((resolve) => {
        this.confirmModal = { show: true, title, message, lines, danger, resolve }
      })
    },

    confirmModalAccept() {
      if (this.confirmModal.resolve) this.confirmModal.resolve(true)
      this.confirmModal.show = false
    },

    confirmModalCancel() {
      if (this.confirmModal.resolve) this.confirmModal.resolve(false)
      this.confirmModal.show = false
    },

    // ════════════════════════════════════════════════════════════════════════
    // Spinner helpers
    // ════════════════════════════════════════════════════════════════════════

    isTableSyncing(tableName) {
      return this.syncingTablesArray.includes(tableName) || this.currentSyncingTable === tableName
    },

    markTableSyncing(tableName) {
      if (!this.syncingTablesArray.includes(tableName)) {
        this.syncingTablesArray = [...this.syncingTablesArray, tableName]
      }
    },

    unmarkTableSyncing(tableName) {
      this.syncingTablesArray = this.syncingTablesArray.filter(t => t !== tableName)
    },

    // ════════════════════════════════════════════════════════════════════════
    // MSSQL → MySQL : tables unidirectionnelles
    // ════════════════════════════════════════════════════════════════════════

    async loadTablesProgressively() {
      this.loading = true
      this.error = null
      
      try {
        const tableNames = await api.getDatabaseSyncTablesNames()
        
        if (!tableNames || tableNames.length === 0) {
          this.error = 'Aucune table configurée pour la synchronisation'
          this.loading = false
          return
        }
        
        this.tables = tableNames.map(name => ({
          name,
          mssqlCount: '...',
          mysqlCount: '...',
          difference: 0,
          lastSync: null,
          status: 'loading',
        }))
        
        this.loading = false
        this.addLog('info', `${tableNames.length} tables trouvées, chargement des statistiques...`)
        
        for (let i = 0; i < tableNames.length; i++) {
          const tableName = tableNames[i]
          try {
            const stats = await api.getDatabaseSyncTableStats(tableName)
            this.tables[i] = stats
          } catch (error) {
            console.error(`Erreur pour la table ${tableName}:`, error)
            this.tables[i] = {
              name: tableName,
              mssqlCount: 0,
              mysqlCount: 0,
              difference: 0,
              lastSync: null,
              status: 'error',
              error: error.message,
            }
          }
        }
        
        this.addLog('info', 'Toutes les statistiques chargées')
      } catch (error) {
        this.error = error.message || 'Erreur lors du chargement des tables'
        this.addLog('error', this.error)
        this.loading = false
      }
    },

    async loadTables() {
      await this.loadTablesProgressively()
    },

    async compareTable(tableName) {
      this.showCompareModal = true
      this.compareLoading = true
      this.compareData = null

      try {
        const data = await api.compareDatabaseSyncTable(tableName)
        this.compareData = data
        this.addLog('info', `Comparaison de la table "${tableName}" effectuée`)
      } catch (error) {
        this.addLog('error', `Erreur lors de la comparaison de "${tableName}": ${error.message}`)
        this.showCompareModal = false
      } finally {
        this.compareLoading = false
      }
    },

    async syncTable(tableName) {
      const confirmed = await this.showConfirm(
        `Synchroniser "${tableName}"`,
        `Synchronisation MSSQL → MySQL pour la table "${tableName}".`,
      )
      if (!confirmed) return

      this.syncing = true
      this.currentSyncingTable = tableName
      this.addLog('info', `Synchronisation de la table "${tableName}" (MSSQL → MySQL)...`)

      try {
        const result = await api.syncDatabaseSyncTable(tableName, false)
        
        if (result.success) {
          this.addLog('success', result.message)
          await this.loadTables()
          if (BIDIRECTIONAL_TABLES.includes(tableName)) {
            await this.loadBiDirStats()
          }
        } else {
          this.addLog('error', result.message || `Erreur lors de la synchronisation de "${tableName}"`)
          
          if (result.errorDetails && result.errorDetails.length > 0) {
            this.addLog('error', `📋 Détails des erreurs (${result.errorDetails.length} première(s)):`)
            result.errorDetails.slice(0, 5).forEach((errDetail, index) => {
              if (errDetail.message) {
                this.addLog('error', `   ${errDetail.message}`)
              } else {
                this.addLog('error', `   ${index + 1}. ID ${errDetail.recordId}: ${errDetail.error}`)
              }
            })
          }
        }
      } catch (error) {
        this.addLog('error', `Erreur lors de la synchronisation de "${tableName}": ${error.message}`)
      } finally {
        this.syncing = false
        this.currentSyncingTable = null
      }
    },

    /**
     * Synchronise toutes les tables MSSQL → MySQL.
     * Les tables bi-directionnelles (visit, control, comment) sont EXCLUES :
     * elles ont leur propre section avec synchronisation dans les deux sens.
     */
    async syncAllTables() {
      const confirmed = await this.showConfirm(
        'Tout synchroniser (MSSQL → MySQL)',
        'Synchronisation de toutes les tables de référence par ordre de dépendance.',
        {
          lines: [
            'Les tables visit, control et comment sont exclues (section bi-directionnelle).',
            'Les tables seront synchronisées niveau par niveau.',
          ],
        }
      )
      if (!confirmed) return

      this.syncing = true
      this.currentSyncLevel = null
      this.totalSyncLevels = 0
      this.syncingTablesArray = []
      this.addLog('info', '🚀 Démarrage de la synchronisation par niveaux (MSSQL → MySQL)...')

      try {
        const levels = await api.getDatabaseSyncTablesLevels()

        if (!levels || levels.length === 0) {
          this.addLog('error', 'Impossible de récupérer les niveaux de tables')
          return
        }

        // Filtrer les niveaux pour exclure les tables bi-directionnelles
        const filteredLevels = levels
          .map(levelData => ({
            ...levelData,
            tables: levelData.tables.filter(t => !BIDIRECTIONAL_TABLES.includes(t)),
          }))
          .filter(levelData => levelData.tables.length > 0)

        this.totalSyncLevels = filteredLevels.length
        this.addLog('info', `${filteredLevels.length} niveaux de synchronisation (tables de référence uniquement)`)

        let globalSuccess = true

        for (const levelData of filteredLevels) {
          const { level, tables } = levelData
          this.currentSyncLevel = level

          this.addLog('info', `⏳ Niveau ${level}/${this.totalSyncLevels} : [${tables.join(', ')}]`)

          // Marquer toutes les tables du niveau comme "en cours"
          tables.forEach(t => this.markTableSyncing(t))

          const levelResults = await Promise.allSettled(
            tables.map(async (tableName) => {
              try {
                return await api.syncDatabaseSyncTable(tableName, false)
              } finally {
                // Retirer le spinner de la table dès qu'elle est terminée
                this.unmarkTableSyncing(tableName)
              }
            })
          )

          let levelSuccess = true
          levelResults.forEach((result, index) => {
            const tableName = tables[index]
            if (result.status === 'fulfilled') {
              const tableResult = result.value
              const type = tableResult.success ? 'success' : 'error'
              this.addLog(type, tableResult.message || `Table "${tableName}" synchronisée`)
              if (!tableResult.success) {
                levelSuccess = false
                globalSuccess = false
              }
            } else {
              this.addLog('error', `Erreur sync "${tableName}": ${result.reason?.message || 'Erreur inconnue'}`)
              levelSuccess = false
              globalSuccess = false
            }
          })

          if (levelSuccess) {
            this.addLog('success', `✅ Niveau ${level} terminé avec succès`)
          } else {
            this.addLog('error', `❌ Niveau ${level} terminé avec des erreurs`)
          }
        }

        if (globalSuccess) {
          this.addLog('success', '🎉 Synchronisation complète (MSSQL → MySQL) terminée avec succès !')
        } else {
          this.addLog('error', '⚠️ Synchronisation (MSSQL → MySQL) terminée avec des erreurs')
        }

        await this.loadTables()

      } catch (error) {
        this.addLog('error', `Erreur lors de la synchronisation globale: ${error.message}`)
      } finally {
        this.syncing = false
        this.currentSyncLevel = null
        this.totalSyncLevels = 0
        this.syncingTablesArray = []
      }
    },

    async testConnection() {
      this.testing = true
      this.addLog('info', 'Test des connexions aux bases de données...')

      try {
        const result = await api.testDatabaseSyncConnection()
        
        if (result.success) {
          this.addLog('success', `✓ Connexions OK - MSSQL & MySQL accessibles`)
          await this.loadTables()
          await this.loadBiDirStats()
        } else {
          this.addLog('error', `✗ Test échoué: ${result.message || 'Erreur inconnue'}`)
        }
      } catch (error) {
        this.addLog('error', `✗ Erreur de connexion: ${error.message}`)
      } finally {
        this.testing = false
      }
    },

    // ════════════════════════════════════════════════════════════════════════
    // Synchronisation bi-directionnelle (visit, control, comment)
    // ════════════════════════════════════════════════════════════════════════

    async loadBiDirStats() {
      this.biDirLoading = true
      this.biDirError = null

      this.biDirTables = BIDIRECTIONAL_TABLES.map(name => ({
        name,
        mssqlToMysql: { mssqlCount: '...', mysqlCount: '...', difference: 0, status: 'loading' },
        mysqlToMssql: { mysqlCount: '...', mssqlCount: '...', difference: 0, status: 'loading' },
      }))

      try {
        const [mssqlToMysqlResults, mysqlToMssqlStats] = await Promise.allSettled([
          Promise.all(BIDIRECTIONAL_TABLES.map(name => api.getDatabaseSyncTableStats(name))),
          api.getVisitSyncToMssqlStats(),
        ])

        if (mssqlToMysqlResults.status === 'fulfilled') {
          mssqlToMysqlResults.value.forEach((stats, i) => {
            if (this.biDirTables[i]) {
              this.biDirTables[i].mssqlToMysql = {
                mssqlCount: stats.mssqlCount,
                mysqlCount: stats.mysqlCount,
                difference: stats.difference,
                status: stats.status,
                lastSync: stats.lastSync,
              }
            }
          })
        } else {
          BIDIRECTIONAL_TABLES.forEach((_, i) => {
            if (this.biDirTables[i]) {
              this.biDirTables[i].mssqlToMysql = {
                mssqlCount: 0, mysqlCount: 0, difference: 0,
                status: 'error',
                error: mssqlToMysqlResults.reason?.message || 'Erreur inconnue',
              }
            }
          })
        }

        if (mysqlToMssqlStats.status === 'fulfilled') {
          const statsArray = mysqlToMssqlStats.value || []
          statsArray.forEach(stats => {
            const tableIndex = this.biDirTables.findIndex(t => t.name === stats.name)
            if (tableIndex !== -1) {
              this.biDirTables[tableIndex].mysqlToMssql = {
                mysqlCount: stats.mysqlCount,
                mssqlCount: stats.mssqlCount,
                difference: stats.difference,
                status: stats.status,
              }
            }
          })
        } else {
          this.biDirTables.forEach((_, i) => {
            this.biDirTables[i].mysqlToMssql = {
              mysqlCount: 0, mssqlCount: 0, difference: 0,
              status: 'error',
              error: mysqlToMssqlStats.reason?.message || 'Erreur inconnue',
            }
          })
        }

        this.biDirTables = [...this.biDirTables]

      } catch (error) {
        this.biDirError = error.message || 'Erreur lors du chargement des statistiques bi-directionnelles'
        this.addLog('error', this.biDirError)
      } finally {
        this.biDirLoading = false
      }
    },

    async syncBiDirTable(tableName, direction) {
      const dirLabel = direction === 'mssql-to-mysql' ? 'MSSQL → MySQL' : 'MySQL → MSSQL'
      const confirmed = await this.showConfirm(
        `Synchroniser "${tableName}"`,
        `Direction : ${dirLabel}`,
      )
      if (!confirmed) return

      this.biDirSyncing = true
      this.biDirSyncingTable = `${tableName}-${direction}`
      this.addLog('info', `Synchronisation de "${tableName}" (${dirLabel})...`)

      try {
        let result
        if (direction === 'mssql-to-mysql') {
          result = await api.syncDatabaseSyncTable(tableName, false)
        } else {
          result = await api.syncVisitTableToMssql(tableName, false)
        }

        const type = result.success ? 'success' : 'error'
        this.addLog(type, result.message || (result.success ? `"${tableName}" synchronisé (${dirLabel})` : 'Erreur'))

        if (!result.success && result.errorDetails?.length) {
          result.errorDetails.slice(0, 5).forEach((detail) => {
            this.addLog('error', `   ID ${detail.recordId ?? '?'}: ${detail.error}`)
          })
        }

        await this.loadBiDirStats()

      } catch (error) {
        this.addLog('error', `Erreur sync "${tableName}" (${dirLabel}): ${error.message}`)
      } finally {
        this.biDirSyncing = false
        this.biDirSyncingTable = null
      }
    },

    async syncAllBiDir() {
      const confirmed = await this.showConfirm(
        'Synchronisation bi-directionnelle complète',
        'Les 3 tables seront synchronisées dans les deux sens.',
        {
          lines: [
            'Étape 1 — MSSQL → MySQL : visit, control, comment',
            'Étape 2 — MySQL → MSSQL : visit, control, comment',
          ],
        }
      )
      if (!confirmed) return

      this.biDirSyncing = true
      this.addLog('info', '🔄 Démarrage de la synchronisation bi-directionnelle (visit, control, comment)...')

      let globalSuccess = true

      try {
        this.addLog('info', '⏳ Étape 1/2 — MSSQL → MySQL...')
        const mssqlToMysqlResults = await Promise.allSettled(
          BIDIRECTIONAL_TABLES.map(name => api.syncDatabaseSyncTable(name, false))
        )

        mssqlToMysqlResults.forEach((res, i) => {
          const name = BIDIRECTIONAL_TABLES[i]
          if (res.status === 'fulfilled') {
            const type = res.value.success ? 'success' : 'error'
            this.addLog(type, res.value.message || `"${name}" MSSQL → MySQL`)
            if (!res.value.success) globalSuccess = false
          } else {
            this.addLog('error', `"${name}" MSSQL → MySQL : ${res.reason?.message || 'Erreur inconnue'}`)
            globalSuccess = false
          }
        })

        this.addLog('info', '⏳ Étape 2/2 — MySQL → MSSQL...')
        const mysqlToMssqlResults = await Promise.allSettled(
          BIDIRECTIONAL_TABLES.map(name => api.syncVisitTableToMssql(name, false))
        )

        mysqlToMssqlResults.forEach((res, i) => {
          const name = BIDIRECTIONAL_TABLES[i]
          if (res.status === 'fulfilled') {
            const type = res.value.success ? 'success' : 'error'
            this.addLog(type, res.value.message || `"${name}" MySQL → MSSQL`)
            if (!res.value.success) globalSuccess = false
          } else {
            this.addLog('error', `"${name}" MySQL → MSSQL : ${res.reason?.message || 'Erreur inconnue'}`)
            globalSuccess = false
          }
        })

        if (globalSuccess) {
          this.addLog('success', '🎉 Synchronisation bi-directionnelle terminée avec succès !')
        } else {
          this.addLog('error', '⚠️ Synchronisation bi-directionnelle terminée avec des erreurs')
        }

        await this.loadBiDirStats()

      } catch (error) {
        this.addLog('error', `Erreur lors de la synchronisation bi-directionnelle: ${error.message}`)
      } finally {
        this.biDirSyncing = false
      }
    },

    // ════════════════════════════════════════════════════════════════════════
    // Helpers partagés
    // ════════════════════════════════════════════════════════════════════════

    addLog(type, message) {
      const now = new Date()
      const time = now.toLocaleTimeString('fr-FR')
      
      this.logs.unshift({
        type,
        message,
        time,
        timestamp: now.getTime(),
      })

      if (this.logs.length > 50) {
        this.logs = this.logs.slice(0, 50)
      }
    },

    clearLogs() {
      this.logs = []
    },

    statusLabel(status) {
      switch (status) {
        case 'synced':   return 'À jour'
        case 'outdated': return 'À synchroniser'
        case 'error':    return 'Erreur'
        case 'loading':  return 'Chargement...'
        default:         return status
      }
    },

    formatCount(value) {
      if (value === undefined || value === null || value === '...') return value ?? 'N/A'
      return typeof value === 'number' ? value.toLocaleString('fr-FR') : value
    },
    
    getNestedValue(obj, path, defaultValue = null) {
      try {
        const value = path.split('.').reduce((current, prop) => current?.[prop], obj)
        return value !== undefined && value !== null ? value : defaultValue
      } catch (e) {
        return defaultValue
      }
    },

    // ── PWA Sync methods ──────────────────────────────────────────────────────

    async loadPwaSyncStatus() {
      this.pwaSyncLoading = true
      try {
        await syncService.init()
        const statuses = await syncService.getSyncStatus()
        const knownEntities = ['properties', 'controls', 'teams', 'users',
          'dictionaries', 'assistanceMandators', 'managerMandators',
          'legalOwners', 'interventionPoints']
        const statusMap = {}
        statuses.forEach(s => { statusMap[s.entity] = s })
        this.pwaSyncStatuses = knownEntities.map(entity => statusMap[entity] || {
          entity,
          lastSync: null,
          status: 'never'
        })
        this.pwaSyncInProgress = syncService._syncProgress?.isSyncing === true
      } catch (error) {
        console.error('Erreur chargement statut sync PWA:', error)
      } finally {
        this.pwaSyncLoading = false
      }
    },

    async resetPwaSyncToNow() {
      const confirmed = await this.showConfirm(
        'Réinitialiser les dates de sync',
        'Les dates de sync PWA seront mises à maintenant. La prochaine sync incrémentale sera vide.',
        { lines: ['Les données en cache ne sont pas supprimées.'] }
      )
      if (!confirmed) return

      this.pwaSyncResetting = true
      try {
        await syncService.init()
        const now = new Date().toISOString()
        const entities = ['properties', 'controls', 'teams', 'users',
          'dictionaries', 'assistanceMandators', 'managerMandators',
          'legalOwners', 'interventionPoints']
        for (const entity of entities) {
          await syncService.resetEntitySyncDate(entity, now)
        }
        this.addLog('success', `✅ Dates de sync PWA réinitialisées à ${new Date().toLocaleString('fr-FR')}`)
        await this.loadPwaSyncStatus()
      } catch (error) {
        this.addLog('error', `Erreur lors de la réinitialisation des dates de sync: ${error.message}`)
      } finally {
        this.pwaSyncResetting = false
      }
    },

    async resetPwaSyncFull() {
      const confirmed = await this.showConfirm(
        'Forcer une resynchronisation complète',
        'Toutes les dates de baseline seront supprimées.',
        {
          lines: [
            'La PWA retéléchargera TOUTES les données au prochain lancement.',
            'À utiliser uniquement si les données sont corrompues.',
          ],
          danger: true,
        }
      )
      if (!confirmed) return

      this.pwaSyncResetting = true
      try {
        await syncService.init()
        await syncService.resetSyncInfo()
        this.addLog('success', '🔄 Dates de sync PWA effacées — resync complète au prochain lancement')
        await this.loadPwaSyncStatus()
      } catch (error) {
        this.addLog('error', `Erreur lors de la réinitialisation complète de la sync: ${error.message}`)
      } finally {
        this.pwaSyncResetting = false
      }
    },

    formatDateTime(isoString) {
      if (!isoString) return '—'
      try {
        return new Date(isoString).toLocaleString('fr-FR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      } catch (e) {
        return isoString
      }
    },

    getRelativeTime(isoString) {
      if (!isoString) return '—'
      try {
        const now = new Date()
        const date = new Date(isoString)
        const diffMs = now - date
        const diffMin = Math.floor(diffMs / 60000)
        const diffH = Math.floor(diffMs / 3600000)
        const diffD = Math.floor(diffMs / 86400000)
        if (diffMin < 1) return 'à l\'instant'
        if (diffMin < 60) return `il y a ${diffMin} min`
        if (diffH < 24) return `il y a ${diffH}h`
        return `il y a ${diffD}j`
      } catch (e) {
        return '—'
      }
    },

    pwaSyncStatusLabel(status) {
      const labels = {
        fresh: '< 1h',
        recent: '< 6h',
        ok: '< 24h',
        outdated: '> 24h',
        never: 'Jamais',
        unknown: 'Inconnu'
      }
      return labels[status] || status
    },
  },
}
</script>

<style scoped>
/* Transition pour la modale de confirmation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
